import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Erb } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Náhľad prílohy, ktorý **nikdy neostane prázdny**: keď obrázok chýba alebo sa
 * nenačíta, na jeho mieste stojí erb mesta.
 *
 * Erb bol dovtedy len za chýbajúcu prílohu (`item.image` bolo `undefined`).
 * Mŕtvu adresu ale položka nesie ako obyčajný `src`, takže z nej v karte
 * zostal rozbitý obrázok prehliadača - sivý štvorec s ikonou, ktorý vyzeral ako
 * chyba appky. Pre kartu je to ten istý stav ako žiadna príloha, preto to rieši
 * jedno miesto pre všetky náhľady: zoznam v paneli, detail nad ním aj stĺpec ID
 * v administrácii.
 *
 * Ošetruje sa aj chyba videa - `onError` má `<video>` rovnako ako `<img>`.
 *
 * `sx` nesie **rám dlaždice** (rozmer, rádius, prípadne border), nie médium
 * v nej: to plochu vždy vyplní celú (`objectFit: 'cover'`).
 */
type Props = {
  /** Obrázok. Chýbajúci **aj** nenačítaný znamená erb. */
  src?: string;
  /** Video namiesto obrázka - kreslí sa jeho prvý frame. */
  video?: string;
  alt?: string;
  /** Výška erbu vo fallbacku; dlaždica má svoj rozmer z `sx`. */
  erbSize?: number;
  sx?: SxProps<Theme>;
};

const MEDIA_SX = {
  width: 1,
  height: 1,
  objectFit: 'cover',
  display: 'block',
} as const;

const ErbThumbnail: React.FC<Props> = ({
  src,
  video,
  alt = '',
  erbSize = 28,
  sx,
}) => {
  const source = src ?? video;

  /**
   * Pamätá si **ktorá** adresa spadla, nie len že niečo spadlo. Karta v zozname
   * si pri dorolovaní ďalšej strany drží inštanciu podľa `key`, takže samotný
   * príznak by novej položke zdedil chybu tej predošlej a erb by sa ukázal aj
   * nad funkčným obrázkom.
   */
  const [brokenSource, setBrokenSource] = React.useState<string>();
  const showMedia = !!source && source !== brokenSource;

  return (
    <Box
      sx={[
        {
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          // Svetlejší podklad než zvyšok karty len pod erbom: pri obrázku ho
          // nie je vidno, pri erbe je erb červený na takmer bielom a nie na
          // sivom poli, ktoré s ním súťaží.
          backgroundColor: showMedia ? 'grey.100' : 'grey.50',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showMedia && src ? (
        <Box
          component='img'
          src={src}
          alt={alt}
          onError={() => setBrokenSource(src)}
          sx={MEDIA_SX}
        />
      ) : showMedia && video ? (
        <Box
          component='video'
          src={`${video}#t=0.1`}
          muted
          playsInline
          preload='metadata'
          onError={() => setBrokenSource(video)}
          sx={MEDIA_SX}
        />
      ) : (
        // `title={null}`: je to dekorácia, názov položky stojí hneď vedľa.
        <Erb variant='primary' size={erbSize} title={null} />
      )}
    </Box>
  );
};

export default ErbThumbnail;
